/**
 * Shared downsizer contribution logic — accumulation super vs commenced ABP (minimum drawdown).
 * Ported verbatim from frontend/src/calculations/downsizerContributionUtils.js.
 */

import { calculateAge } from './support/dateHelpers.ts';
import { DOWNSIZER_CONTRIBUTION_CAP, PENSION_AGE } from './constants/ratesAndThresholds.ts';
import { getAnnualMinimumAbpDraw } from './abpProjectionUtils.ts';
import type { FactFind, FactFindAssetMap } from './factFindTypes.ts';

/** Minimum age to make a downsizer contribution (ATO). */
export const DOWNSIZER_MIN_AGE = 55;

/**
 * What a strategy-created super balance is assumed to EARN — the client's own new-money investment
 * profile, from the single published table (investmentProfiles.ts), Conservative until the client
 * answers "how would new money be invested?". Super and pension are wrappers around the same
 * investments: within a profile the wrapper changes the tax on the earnings (15% accumulation, nil
 * pension), never the earnings themselves — full history and rationale in investmentProfiles.ts
 * and docs/actuarial-review/D_ASSUMPTIONS.md §D.2.7.
 */
import { INVESTMENT_PROFILES, SUPER_ACCUMULATION_EARNINGS_TAX_RATE, newMoneyProfileOf, strategyAccumulationNetPctFor } from './investmentProfiles.ts';
import { pensionHeadroomOf } from './transferBalanceCap.ts';
export { SUPER_ACCUMULATION_EARNINGS_TAX_RATE };
/** The High growth (100% growth assets) rates — the ceiling of the table. Accumulation is the
 *  componentised-fund-tax derivation (15% on grossed-up income less refundable franking credits,
 *  10% on growth), not the old flat 15% of everything — see investmentProfiles.fundTaxDragPct. */
export const STRATEGY_ABP_RETURN_PCT = INVESTMENT_PROFILES.high_growth.pensionNetPct;
export const STRATEGY_ACCUMULATION_RETURN_PCT = strategyAccumulationNetPctFor('high_growth');

/** Minimum age to model commencing an account-based pension from downsizer proceeds. */
export const DOWNSIZER_ABP_COMMENCE_MIN_AGE = 60;

export function getOwnerAgeFromFactFind(factFind: FactFind, ownership: string = 'client'): number | null {
    if (ownership === 'partner' && factFind?.partner?.dob) {
        return calculateAge(factFind.partner.dob);
    }
    if (factFind?.client?.dob) {
        return calculateAge(factFind.client.dob);
    }
    return null;
}

export function isDownsizerContributionEligible(factFind: FactFind, ownership: string = 'client'): boolean {
    const age = getOwnerAgeFromFactFind(factFind, ownership);
    return age != null && age >= DOWNSIZER_MIN_AGE;
}

export function canCommenceAbpFromDownsizer(factFind: FactFind, ownership: string = 'client'): boolean {
    const age = getOwnerAgeFromFactFind(factFind, ownership);
    return age != null && age >= DOWNSIZER_ABP_COMMENCE_MIN_AGE;
}

function resolveDownsizerAssetId(options: any, ownership: string): string {
    if (options.assetId && ownership === 'client') return options.assetId;
    if (options.partnerAssetId && ownership === 'partner') return options.partnerAssetId;
    const idPrefix = options.idPrefix || 'downsizer';
    return ownership === 'partner' ? `${idPrefix}-partner` : idPrefix;
}

/** Book a downsizer contribution as accumulation super or a new ABP (min drawdown income). */
export function applyDownsizerContributionToAssets(assets: FactFindAssetMap, factFind: FactFind, options: any = {}): void {
    const amount = Math.max(0, options.amount || 0);
    if (amount <= 0 || !assets) return;

    const ownership = options.ownership || 'client';
    const commenceIncomeStream = options.commenceIncomeStream === true;
    const ownerAge = getOwnerAgeFromFactFind(factFind, ownership);
    const asAbp = commenceIncomeStream && canCommenceAbpFromDownsizer(factFind, ownership);
    // The same booking serves an after-tax contribution (nonConcessionalContributions.ts), which
    // names its assets differently so a balance sheet says which rule put the money there.
    const labels = {
        abp: options.labels?.abp ?? 'Downsizer ABP',
        capped: options.labels?.capped ?? 'Downsizer to super (pension cap reached)',
        contribution: options.labels?.contribution ?? 'Downsizer Contribution',
    };
    const assetId = resolveDownsizerAssetId(options, ownership);
    // Priced at the client's new-money profile (Conservative until they answer) — the same
    // portfolio the strategy's personal-investment leg assumes, in the wrapper's tax treatment.
    const profileId = newMoneyProfileOf(factFind);
    const profileRow = INVESTMENT_PROFILES[profileId];
    const accumulationRatePct = strategyAccumulationNetPctFor(profileId);

    if (asAbp) {
        // The Transfer Balance Cap: a person can only move their remaining headroom into a
        // retirement-phase pension — recommending more is recommending something the law does not
        // allow. The pension takes the headroom; the excess is booked to accumulation super under
        // a name that says why, so it is visible in every net-worth breakdown.
        const headroom = pensionHeadroomOf(factFind, ownership === 'partner' ? 'partner' : 'client');
        const toPension = Math.min(amount, headroom);
        const toAccumulation = amount - toPension;

        if (toPension > 0) {
            if (!assets.accountBasedPensions) assets.accountBasedPensions = [];
            const annualIncome = getAnnualMinimumAbpDraw(toPension, ownerAge);
            assets.accountBasedPensions.push({
                id: `${assetId}-abp`,
                type: 'accountBasedPensions',
                description: ownership === 'partner' ? `${labels.abp} (Partner)` : labels.abp,
                value: toPension,
                ownership,
                income: annualIncome,
                growthRate: profileRow.pensionNetPct,
                __abpFundedAcquisition: true,
            });
        }
        if (toAccumulation > 0) {
            if (!assets.superannuation) assets.superannuation = [];
            assets.superannuation.push({
                id: `${assetId}-capped`,
                type: 'superannuation',
                description: ownership === 'partner' ? `Partner ${labels.capped}` : labels.capped,
                value: toAccumulation,
                ownership,
                growthRate: accumulationRatePct,
            });
        }
        return;
    }

    if (!assets.superannuation) assets.superannuation = [];
    assets.superannuation.push({
        id: assetId,
        type: 'superannuation',
        description: ownership === 'partner' ? `Partner ${labels.contribution}` : labels.contribution,
        value: amount,
        ownership,
        growthRate: accumulationRatePct,
    });
}

export interface DownsizerAllocation {
    /** Amount into the client's own super. */
    client: number;
    /** Amount into the partner's super — always 0 for a single client. */
    partner: number;
    /**
     * The member filled FIRST because their accumulation super escapes BOTH means tests (they are
     * under Age Pension age), or `null` when the ordering makes no difference to the Age Pension.
     * Callers use this to explain the split; nothing else should infer it.
     */
    preferred: 'client' | 'partner' | null;
}

/**
 * Split a downsizer contribution between the two members — filling the member whose super is
 * EXEMPT first.
 *
 * WHY THIS EXISTS. Every generator used to split the contribution age-blind: Retirement Village
 * halved it, the Downsizer module and Support at Home filled the client first. All three ignored
 * the fact that decides the answer — accumulation super held by someone UNDER Age Pension age is
 * exempt from both means tests, and the same money in an older member's name is counted in full.
 * On a real age-gap couple that is worth over $12,000 a year (measured in
 * server/tests/rest/calculation/youngerSpouseSuperExemption.test.ts), which is larger than most of
 * the other levers in these modules combined, and the engine has always priced it correctly. Only
 * the strategies were leaving it on the table.
 *
 * The preference applies ONLY when exactly one member is both contribution-eligible (55+) and under
 * Age Pension age. When both are exempt, or neither is, the ordering cannot change the pension, so
 * the amount is split evenly — which also spreads a later pension commencement across two transfer
 * balance caps instead of concentrating it against one.
 *
 * `caps` lets a caller impose its own per-person ceiling (Support at Home's scenario amounts); the
 * statutory per-person cap always applies on top.
 */
export interface DownsizerPreference {
    /** The member to fill first, or null when the ordering cannot change the Age Pension. */
    preferred: 'client' | 'partner' | null;
    clientEligible: boolean;
    partnerEligible: boolean;
    /** The preferred member's age today — null when there is no preference. */
    preferredAge: number | null;
}

/**
 * WHOSE super the contribution should go into, and why — the single source both the strategy
 * generators and the UI read, so a card can never recommend one split while the panel explains
 * another.
 */
export function downsizerExemptionPreference(factFind: FactFind): DownsizerPreference {
    const isCouple = factFind?.adviceFor === 'couple';
    const clientAge = getOwnerAgeFromFactFind(factFind, 'client');
    const partnerAge = isCouple ? getOwnerAgeFromFactFind(factFind, 'partner') : null;

    const clientEligible = clientAge != null && clientAge >= DOWNSIZER_MIN_AGE;
    const partnerEligible = isCouple && partnerAge != null && partnerAge >= DOWNSIZER_MIN_AGE;

    // "Exempt" here means the means-test exemption on accumulation super below Age Pension age —
    // the only reason one member's name beats the other's.
    const clientExempt = clientEligible && (clientAge as number) < PENSION_AGE;
    const partnerExempt = partnerEligible && (partnerAge as number) < PENSION_AGE;

    const preferred: 'client' | 'partner' | null =
        clientExempt && !partnerExempt ? 'client' : partnerExempt && !clientExempt ? 'partner' : null;

    return {
        preferred,
        clientEligible,
        partnerEligible,
        preferredAge: preferred === 'client' ? clientAge : preferred === 'partner' ? partnerAge : null,
    };
}

export function allocateDownsizerContribution(
    factFind: FactFind,
    available: number,
    caps: { client?: number; partner?: number } = {},
): DownsizerAllocation {
    const { preferred, clientEligible, partnerEligible } = downsizerExemptionPreference(factFind);

    const clientCap = Math.min(DOWNSIZER_CONTRIBUTION_CAP, Math.max(0, caps.client ?? DOWNSIZER_CONTRIBUTION_CAP));
    const partnerCap = Math.min(DOWNSIZER_CONTRIBUTION_CAP, Math.max(0, caps.partner ?? DOWNSIZER_CONTRIBUTION_CAP));

    let remaining = Math.max(0, available || 0);
    let client = 0;
    let partner = 0;

    if (preferred === 'client' && clientEligible) {
        client = Math.min(clientCap, remaining);
        remaining -= client;
        if (partnerEligible) partner = Math.min(partnerCap, remaining);
    } else if (preferred === 'partner' && partnerEligible) {
        partner = Math.min(partnerCap, remaining);
        remaining -= partner;
        if (clientEligible) client = Math.min(clientCap, remaining);
    } else if (clientEligible && partnerEligible) {
        client = Math.min(clientCap, Math.round(remaining / 2));
        remaining -= client;
        partner = Math.min(partnerCap, remaining);
    } else if (clientEligible) {
        client = Math.min(clientCap, remaining);
    } else if (partnerEligible) {
        partner = Math.min(partnerCap, remaining);
    }

    return { client, partner, preferred };
}

export function applyDownsizerContributionsToAssets(assets: FactFindAssetMap, factFind: FactFind, details: any = {}, options: any = {}): void {
    const commenceIncomeStream = details.commenceIncomeStream === true;

    applyDownsizerContributionToAssets(assets, factFind, {
        amount: details.downsizerContribution,
        ownership: 'client',
        commenceIncomeStream,
        idPrefix: options.idPrefix,
        assetId: options.assetId,
    });

    applyDownsizerContributionToAssets(assets, factFind, {
        amount: details.partnerDownsizerContribution,
        ownership: 'partner',
        commenceIncomeStream,
        idPrefix: options.partnerIdPrefix || options.idPrefix,
        partnerAssetId: options.partnerAssetId,
    });
}
